import React, { useContext } from 'react';
import { Avatar, Box, Chip, Divider, Typography } from '@mui/material';
import { roleContext } from '../../context/RoleContext';
import InfoChangeDialog from './InfoChangeDialog';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { UserRole } from '../../types/role';
import { useProfileData } from '../../context/ProfileDataContext';

const ACCENT = '#dd8709';

const getRoleInfo = (role: UserRole) => {
  switch (role) {
    case UserRole.SHOP_OWNER:
      return { label: 'Shop Owner', Icon: StorefrontIcon, color: '#e65100' };
    case UserRole.BARBER:
      return { label: 'Barber', Icon: ContentCutIcon, color: '#1565c0' };
    case UserRole.SHOP_BARBER:
      return { label: 'Shop Barber', Icon: ContentCutIcon, color: '#2e7d32' };
    default:
      return { label: 'User', Icon: WorkOutlineIcon, color: '#555' };
  }
};

interface Row {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow = ({ icon, label, value }: Row) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25 }}>
    <Box sx={{ color: ACCENT, mt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography
        variant="caption"
        sx={{ color: '#999', lineHeight: 1.2, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'GFS Didot',
          fontWeight: 600,
          color: '#1a1a1a',
          wordBreak: 'break-word',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

export default function Details() {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const role = useContext(roleContext);
  const { partner } = useProfileData();

  const details = {
    firstName: partner?.firstName || '',
    lastName: partner?.lastName || '',
    phoneNumber: partner?.phoneNumber || '',
  };

  const roleInfo = getRoleInfo(role as UserRole);
  const { Icon: RoleIcon } = roleInfo;
  const initials =
    `${details.firstName?.[0] ?? ''}${
      details.lastName?.[0] ?? ''
    }`.toUpperCase() || '?';
  const fullName =
    [details.firstName, details.lastName].filter(Boolean).join(' ') ||
    'Unknown';

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
        bgcolor: '#fff',
        mb: '20px',
      }}
    >
      {/* ── Coloured header strip ── */}
      <Box
        sx={{
          bgcolor: '#2b2b33',
          px: 3,
          pt: 3,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: ACCENT,
            fontSize: 24,
            fontWeight: 'bold',
            border: '3px solid rgba(255,255,255,0.2)',
          }}
        >
          {initials}
        </Avatar>

        <Typography
          variant="subtitle1"
          sx={{
            color: '#fff',
            fontFamily: 'GFS Didot',
            fontWeight: 700,
            mt: 0.5,
          }}
        >
          {fullName}
        </Typography>

        <Chip
          icon={
            <RoleIcon
              sx={{ fontSize: 14, color: `${roleInfo.color} !important` }}
            />
          }
          label={roleInfo.label}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontFamily: 'GFS Didot',
            fontSize: 11,
            fontWeight: 600,
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />
      </Box>

      {/* ── Info rows ── */}
      <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5, textAlign: 'left' }}>
        <InfoRow
          icon={<EmailOutlinedIcon fontSize="small" />}
          label="Email"
          value={user.email || '—'}
        />
        <Divider sx={{ borderColor: '#4c2c2c' }} />
        <InfoRow
          icon={<LocalPhoneOutlinedIcon fontSize="small" />}
          label="Phone"
          value={details.phoneNumber || 'Not provided'}
        />
      </Box>

      {/* ── Edit button ── */}
      <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'center' }}>
        <InfoChangeDialog />
      </Box>
    </Box>
  );
}
